import { AxiosError } from 'axios';

/**
 * API Client error structure (from API response interceptor)
 */
export interface ApiClientError {
  message: string;
  status: number;
  code?: string;
  details?: unknown;
}

/**
 * Standard error response structure from API
 */
interface ApiErrorResponse {
  success: false;
  data: null;
  error: {
    code: string;
    message: string | string[];
    details?: unknown;
  };
  meta: {
    timestamp: string;
    path: string;
  };
}

/**
 * User-friendly error messages mapped from API error codes
 */
const ERROR_MESSAGES: Record<string, string> = {
  // Validation errors
  VALIDATION_ERROR: 'Please check your input and try again',
  INVALID_EMAIL: 'Please enter a valid email address',
  INVALID_PHONE: 'Please enter a valid phone number',
  INVALID_POSTAL_CODE: 'Please enter a valid postal code',

  // Resource errors
  NOT_FOUND: 'The requested information was not found',
  FAMILY_NOT_FOUND: 'Family not found',
  USER_NOT_FOUND: 'User not found',
  EVENT_NOT_FOUND: 'Event not found',

  // Conflict errors
  CONFLICT: 'This information already exists',
  EMAIL_EXISTS: 'An account with this email already exists',
  DUPLICATE_ENTRY: 'This entry already exists',

  // Authorization errors
  UNAUTHORIZED: 'Please sign in to continue',
  FORBIDDEN: "You don't have permission to perform this action",
  SESSION_EXPIRED: 'Your session has expired. Please sign in again',

  // Server errors
  INTERNAL_SERVER_ERROR: 'Something went wrong. Please try again later',
  SERVICE_UNAVAILABLE: 'Service is temporarily unavailable',
  TIMEOUT: 'Request timed out. Please try again',

  // Network errors
  NETWORK_ERROR: 'Unable to connect. Please check your internet connection',
  OFFLINE: 'You appear to be offline. Please check your connection',

  // Family registration specific
  FAMILY_REGISTRATION_FAILED: 'Failed to register family. Please try again',
  ADULT_CREATION_FAILED: 'Failed to add family member. Please try again',
  CHILD_CREATION_FAILED: 'Failed to add child. Please try again',
  RELATIONSHIP_CREATION_FAILED: 'Failed to create family relationships. Please try again',
};

/**
 * Checks if error is an ApiClientError (from API response interceptor)
 */
function isApiClientError(error: unknown): error is ApiClientError {
  return error !== null && typeof error === 'object' && 'message' in error && 'status' in error;
}

export const getErrorMessage = (error: unknown): string => {
  if (isApiClientError(error)) {
    if (error.code && ERROR_MESSAGES[error.code]) {
      return ERROR_MESSAGES[error.code];
    }

    return error.message || 'An error occurred';
  }

  if (isAxiosError(error)) {
    const apiError = error.response?.data as ApiErrorResponse | undefined;

    if (apiError?.error) {
      const { code, message } = apiError.error;

      if (code && ERROR_MESSAGES[code]) {
        return ERROR_MESSAGES[code];
      }

      if (typeof message === 'string') {
        return message;
      }

      if (Array.isArray(message) && message.length > 0) {
        return message[0];
      }
    }

    if (error.response?.status) {
      switch (error.response.status) {
        case 400:
          return ERROR_MESSAGES.VALIDATION_ERROR;
        case 401:
          return ERROR_MESSAGES.UNAUTHORIZED;
        case 403:
          return ERROR_MESSAGES.FORBIDDEN;
        case 404:
          return ERROR_MESSAGES.NOT_FOUND;
        case 409:
          return ERROR_MESSAGES.CONFLICT;
        case 422:
          return ERROR_MESSAGES.VALIDATION_ERROR;
        case 500:
          return ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
        case 503:
          return ERROR_MESSAGES.SERVICE_UNAVAILABLE;
        default:
          return 'An unexpected error occurred. Please try again';
      }
    }

    if (error.code === 'ERR_NETWORK') {
      return ERROR_MESSAGES.NETWORK_ERROR;
    }

    if (error.code === 'ECONNABORTED') {
      return ERROR_MESSAGES.TIMEOUT;
    }

    return error.message || 'An error occurred while processing your request';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
};

/**
 * Type guard for Axios errors
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (error as AxiosError).isAxiosError === true;
}

/**
 * Enhanced error object with user-friendly message
 */
export interface EnhancedError {
  /** User-friendly message to display */
  userMessage: string;
  /** Original error for logging */
  originalError: unknown;
  /** Error code from API (if available) */
  code?: string;
  /** HTTP status code (if available) */
  statusCode?: number;
}

/**
 * Transforms any error into an enhanced error with user-friendly message
 *
 * @param error - Error to transform
 * @param context - Additional context about where the error occurred
 * @returns Enhanced error object
 *
 * @example
 * try {
 *   await registerFamily(data);
 * } catch (error) {
 *   const enhanced = transformError(error, 'family registration');
 *   console.error('Error during', context, ':', enhanced.originalError);
 *   toast.error(enhanced.userMessage);
 * }
 */
export const transformError = (error: unknown, context?: string): EnhancedError => {
  const userMessage = getErrorMessage(error);

  let code: string | undefined;
  let statusCode: number | undefined;

  if (isApiClientError(error)) {
    code = error.code;
    statusCode = error.status;
  } else if (isAxiosError(error)) {
    const apiError = error.response?.data as ApiErrorResponse | undefined;
    code = apiError?.error?.code;
    statusCode = error.response?.status;
  }

  // Log the full error for debugging
  if (context) {
    console.error(`Error during ${context}:`, error);
  } else {
    console.error('Error occurred:', error);
  }

  return {
    userMessage,
    originalError: error,
    code,
    statusCode,
  };
};

export const handleError = (error: unknown, context?: string): string => {
  const enhanced = transformError(error, context);
  return enhanced.userMessage;
};

export const extractErrorCode = (error: unknown): { code?: string; details?: unknown } | null => {
  if (isApiClientError(error)) {
    return {
      code: error.code,
      details: error.details,
    };
  }

  if (isAxiosError(error)) {
    const apiError = error.response?.data as ApiErrorResponse | undefined;
    return {
      code: apiError?.error?.code,
      details: apiError?.error?.details,
    };
  }

  return null;
};
